/**
 * Metric Card Component
 * Displays key metrics with sparklines, colored accents, and trend chips.
 * Theme-aware (reads palette from ThemeContext) with an optional `accent`
 * variant that highlights the card with the brand accent colour.
 */

import React from 'react';
import { Card, CardContent, Box, Typography, Chip, IconButton } from '@mui/material';
import { Icon } from '@iconify/react';
import { AreaChart, Area, ResponsiveContainer } from 'recharts';
import { useThemeMode, numericSx, radius } from '../../context/ThemeContext';

const TREND_ICONS = {
  up: 'solar:arrow-up-outline',
  down: 'solar:arrow-down-outline',
  flat: 'solar:minus-square-outline',
};

function MetricCard({
  title,
  value,
  subtitle,
  caption,           // small italic line at the bottom of the card (e.g. "Updated · 24 Dec 2025")
  icon,
  trend,
  trendValue,
  color = 'primary',
  invertTrend = false, // e.g. NGN/USD: a RISING value reads as caution, not a win
  accent = false,     // highlight this card with the brand accent (green)
  dark = false,       // deep navy fill with light text (matches NiceAdmin "Monthly Sales")
  sparklineData,
  onClick,
}) {
  const { palette } = useThemeMode();

  // Map `color` prop to a theme-derived accent colour for this card
  const colorMap = {
    primary: palette.text,
    secondary: palette.accent,
    info: palette.info,
    warning: palette.warning,
    error: palette.danger,
    success: palette.accent,
  };
  const accentColor = accent ? palette.accent : (colorMap[color] || colorMap.primary);

  const getTrendChip = () => {
    if (!trend || !trendValue) return null;
    const isUp = trend === 'up';
    const isDown = trend === 'down';
    const goodDirection = invertTrend ? isDown : isUp;
    const badDirection = invertTrend ? isUp : isDown;
    const chipBg = goodDirection ? palette.accentBg : badDirection ? palette.dangerBg : palette.surfaceHover;
    const chipColor = goodDirection ? palette.accent : badDirection ? palette.danger : palette.textMuted;
    const iconName = isUp ? TREND_ICONS.up : isDown ? TREND_ICONS.down : TREND_ICONS.flat;
    return (
      <Chip
        size="small"
        icon={<Icon icon={iconName} width={14} height={14} color={chipColor} style={{ marginLeft: 6 }} />}
        label={trendValue}
        sx={{
          backgroundColor: chipBg,
          color: chipColor,
          fontWeight: 600,
          fontSize: '0.72rem',
          height: 22,
          '& .MuiChip-label': { px: 0.6 },
        }}
      />
    );
  };

  const chartData = sparklineData ? sparklineData.map((v, i) => ({ v, i })) : null;
  const sparkId = `spark-${color}-${accent ? 'a' : 'n'}`;

  // Three visual variants (F system: cards stay white, emphasis comes from
  // accent details, never a solid fill):
  // 1. `accent` - white card with accent border, title, and icon chip
  // 2. `dark`   - deep navy bg, light text (reserved, rarely used)
  // 3. default  - plain card background with palette text
  const cardBg = dark ? '#0F2744' : palette.card;
  const cardBorder = accent ? `${palette.accent}66` : dark ? '#0F2744' : palette.cardBorder;
  const titleColor = accent ? palette.accent : dark ? 'rgba(241,245,249,0.75)' : palette.textMuted;
  const valueColor = dark ? '#F1F5F9' : palette.text;
  const subtitleColor = dark ? 'rgba(241,245,249,0.7)' : palette.textMuted;
  const iconBg = accent ? palette.accentBg : dark ? 'rgba(34,197,94,0.18)' : palette.surfaceHover;
  const iconColor = accent ? palette.accent : dark ? '#22C55E' : palette.text;

  const restingShadow = palette.shadowSm;
  const hoverShadow = palette.shadowMd;

  return (
    <Card
      onClick={onClick}
      sx={{
        height: '100%',
        bgcolor: cardBg,
        border: `1px solid ${cardBorder}`,
        borderRadius: `${radius.card}px`,
        boxShadow: restingShadow,
        cursor: onClick ? 'pointer' : 'default',
        transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
        '&:hover': onClick ? {
          boxShadow: hoverShadow,
          borderColor: accent ? cardBorder : palette.accent,
        } : undefined,
      }}
    >
      <CardContent sx={{ p: 2.5, position: 'relative', overflow: 'hidden', '&:last-child': { pb: 2.5 } }}>
        {/* Background sparkline - all variants (the accent card is white now) */}
        {chartData && (
          <Box
            sx={{
              position: 'absolute', bottom: 0, left: 0, right: 0,
              height: 48, opacity: dark ? 0.45 : 0.18, pointerEvents: 'none',
            }}
          >
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData} margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id={sparkId} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={dark ? '#22C55E' : accentColor} stopOpacity={0.6} />
                    <stop offset="100%" stopColor={dark ? '#22C55E' : accentColor} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <Area type="monotone" dataKey="v" stroke={dark ? '#22C55E' : accentColor} strokeWidth={1.5} fill={`url(#${sparkId})`} />
              </AreaChart>
            </ResponsiveContainer>
          </Box>
        )}

        {/* Header row: title (left) + action arrow (right) */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2, position: 'relative' }}>
          <Typography
            variant="body2"
            sx={{
              color: titleColor,
              fontWeight: 500,
              fontSize: '0.82rem',
              letterSpacing: '0.01em',
            }}
          >
            {title}
          </Typography>
          <IconButton
            size="small"
            onClick={onClick}
            aria-label={typeof title === 'string' ? title : 'Card action'}
            tabIndex={onClick ? 0 : -1}
            sx={{
              width: 36, height: 36,
              bgcolor: iconBg,
              color: iconColor,
              flexShrink: 0,
              transition: 'all 0.2s',
              '& svg': { fontSize: 18 },
              '&:hover': {
                bgcolor: palette.accentBg,
                color: palette.accent,
              },
            }}
          >
            {icon || <Icon icon="solar:arrow-right-up-outline" width={18} height={18} />}
          </IconButton>
        </Box>

        {/* Value */}
        <Typography
          sx={{
            ...numericSx,
            fontWeight: 700,
            color: valueColor,
            fontSize: '2rem',
            lineHeight: 1,
            position: 'relative',
            mb: (subtitle || trendValue) ? 1.2 : 0,
          }}
        >
          {value}
        </Typography>

        {(subtitle || trendValue) && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', position: 'relative' }}>
            {getTrendChip()}
            {subtitle && (
              <Typography variant="body2" sx={{ color: subtitleColor, fontSize: '0.78rem', lineHeight: 1.35 }}>
                {subtitle}
              </Typography>
            )}
          </Box>
        )}

        {caption && (
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              color: subtitleColor,
              fontStyle: 'italic',
              fontSize: '0.7rem',
              mt: 1.25,
              position: 'relative',
            }}
          >
            {caption}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

export default MetricCard;
